import styled from "@emotion/styled";
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { MoreHoriz, QrCodeScanner } from "@mui/icons-material";
import { memo, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BottomDrawer } from "@common/BottomDrawer/BottomDrawer";

type MenuConfigItem = {
  text: string;
  icon: React.ReactNode;
  path: string;
  isMobile: boolean;
  mobilePrimary?: boolean;
  requiresOrgMember?: boolean;
};

type MobileMenuProps = {
  menuConfig: MenuConfigItem[];
  isOrganizationMember: boolean;
};

export const MobileMenu = memo(
  ({ menuConfig, isOrganizationMember }: MobileMenuProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [moreOpen, setMoreOpen] = useState(false);

    const primaryItems = useMemo(
      () =>
        menuConfig.filter(
          (item) =>
            item.isMobile &&
            item.mobilePrimary &&
            (!item.requiresOrgMember || isOrganizationMember)
        ),
      [menuConfig, isOrganizationMember]
    );

    const moreItems = useMemo(
      () =>
        menuConfig.filter(
          (item) =>
            item.isMobile &&
            !item.mobilePrimary &&
            (!item.requiresOrgMember || isOrganizationMember)
        ),
      [menuConfig, isOrganizationMember]
    );

    const activeValue = (() => {
      const path = location.pathname;
      if (path.startsWith("/scanner")) return "scanner";
      const primary = primaryItems.find((item) => path.startsWith(item.path.split("/").slice(0, 2).join("/")));
      return primary?.path ?? "";
    })();

    const handleNavigate = (path: string) => {
      navigate(path);
      setMoreOpen(false);
    };

    return (
      <>
        <MenuContainer>
          <Paper elevation={3} sx={{ width: "100vw", paddingBottom: "20px" }}>
            <BottomNavigation
              sx={{ width: "100vw" }}
              showLabels
              value={activeValue}
            >
              {primaryItems.map((item) => (
                <BottomNavigationAction
                  key={item.text}
                  label={item.text}
                  icon={item.icon}
                  value={item.path}
                  onClick={() => handleNavigate(item.path)}
                />
              ))}
              <BottomNavigationAction
                label="Scanner"
                icon={<QrCodeScanner />}
                value="scanner"
                onClick={() => handleNavigate("/scanner")}
              />
              <BottomNavigationAction
                label="More"
                icon={<MoreHoriz />}
                value="more"
                onClick={() => setMoreOpen(true)}
              />
            </BottomNavigation>
          </Paper>
        </MenuContainer>

        <BottomDrawer open={moreOpen} onClose={() => setMoreOpen(false)}>
          <Box sx={{ pb: `calc(env(safe-area-inset-bottom) + 16px)` }}>
            <Typography
              variant="caption"
              sx={{ px: 2, pt: 2, pb: 1, display: "block", opacity: 0.5, textTransform: "uppercase", letterSpacing: 1 }}
            >
              More
            </Typography>
            <List disablePadding>
              {moreItems.map((item) => (
                <ListItemButton
                  key={item.text}
                  onClick={() => handleNavigate(item.path)}
                  selected={location.pathname.startsWith(
                    item.path.split("/").slice(0, 2).join("/")
                  )}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </BottomDrawer>
      </>
    );
  }
);

const MenuContainer = styled.div`
  display: flex;
  position: fixed;
  bottom: 0;
  z-index: 2;
  left: 0;
  right: 0;
  padding-bottom: env(safe-area-inset-bottom);
  @media (min-width: 769px) {
    display: none;
  }
`;
